import * as stylex from "@stylexjs/stylex";
import { Plus } from "lucide-react";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, motion, radii, shadows } from "@/theme/tokens.stylex";
import { Icon } from "./Icon";
import { press } from "./press";
type NotePadSize = "sm" | "lg";
type NotePadProps = {
  label?: string;
  onClick?: () => void;
  size?: NotePadSize;
  xstyle?: stylex.StyleXStyles;
};
type Dimensions = {
  width: number;
  height: number;
  padding: number;
  ruleGap: number;
  button: number;
  glyph: number;
};
const DIMENSIONS: Record<NotePadSize, Dimensions> = {
  sm: {
    width: 128,
    height: 116,
    padding: 15,
    ruleGap: 13,
    button: 40,
    glyph: 20
  },
  lg: {
    width: 220,
    height: 200,
    padding: 26,
    ruleGap: 22,
    button: 56,
    glyph: 28
  }
};
const _temp = {
  k1xSpc: "x78zum5",
  kXwgrk: "xdt5ytf",
  "$$css": true
};
const _temp2 = {
  kSGwAc: "xpvyfi4",
  kaIpWk: "xqhrb7l",
  kWkggS: "x163aph8",
  kMwMTN: "xphs7x8",
  k1xSpc: "x78zum5",
  kGNEyG: "x6s0dn4",
  kjj79g: "xl56j7k",
  "$$css": true
};
const styles = {
  root: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x1rg5ohu",
    kmuXW: "x2lah0s",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kmVPX3: "x1717udv",
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x11xpdln",
    kIyJzY: "x1bpocu2",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  size: (width: number, height: number) => [{
    kzqmXN: width != null ? "x5lhr3w" : width,
    kZKoxP: height != null ? "x16ye13r" : height,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(height)
  }],
  sheet: {
    kVAEAm: "x10l6tqk",
    kpwlN0: "x10a8y8t",
    kaIpWk: "x1wz39yh",
    kWkggS: "x1hueysq",
    kGVxlE: "xuobv6a",
    k1ekBW: "x15406qy",
    kIyJzY: "x11l8gmq",
    kAMwcw: "x1eyvnp3",
    $$css: true
  },
  top: {
    k3aq6I: "x1d74iyf",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kjj79g: "x1qughib",
    $$css: true
  },
  padding: (padding: number) => [{
    kmVPX3: padding != null ? "x1fozly0" : padding,
    $$css: true
  }, {
    "--x-padding": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(padding)
  }],
  rules: (gap: number) => [_temp, {
    kOIVth: gap != null ? "x1pidvrl" : gap,
    $$css: true
  }, {
    "--x-gap": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(gap)
  }],
  plus: (size: number) => [_temp2, {
    kzqmXN: size != null ? "x5lhr3w" : size,
    kZKoxP: size != null ? "x16ye13r" : size,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size)
  }]
};
export function NotePad({
  label,
  onClick,
  size = "sm",
  xstyle
}: NotePadProps) {
  const {
    width,
    height,
    padding,
    ruleGap,
    button,
    glyph
  } = DIMENSIONS[size];
  const sheets = <>
      <span className="x10l6tqk x10a8y8t x1wz39yh x1hueysq xuobv6a x15406qy x11l8gmq x1eyvnp3 xs4581t" />
      <span className="x10l6tqk x10a8y8t x1wz39yh x1hueysq xuobv6a x15406qy x11l8gmq x1eyvnp3 x1umilfq" />
      <span {...stylex.props(styles.sheet, styles.top, styles.padding(padding))}>
        <span {...stylex.props(styles.rules(ruleGap))}>
          <span className="xjm9jq1 xuv7tap" />
          <span className="xjm9jq1 xuv7tap" />
          <span className="xjm9jq1 xuv7tap xycev2y" />
        </span>
        {onClick && <span {...stylex.props(styles.plus(button))}>
            <Icon icon={Plus} size={glyph} />
          </span>}
      </span>
    </>;
  if (onClick === undefined) {
    return <span aria-hidden="true" {...stylex.props(styles.root, styles.size(width, height), xstyle)}>
        {sheets}
      </span>;
  }
  return <button type="button" aria-label={label} onClick={onClick} {...stylex.props(styles.root, styles.size(width, height), press.scale, xstyle)}>
      {sheets}
    </button>;
}